import type { ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { MudCarpFish } from "./MudCarpFish";
import { useAuth, KICK_LOGIN_URL } from "@/lib/auth";

type Props = { children: ReactNode };

export function Layout({ children }: Props) {
  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function Header() {
  const auth = useAuth();
  const isAdmin = !auth.loading && auth.user?.role === "Admin";

  return (
    <header className="sticky top-0 z-40 bg-carp-paper border-b-[3px] border-carp-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link to="/" className="group inline-flex items-center gap-3">
          <span className="block w-12 h-8 overflow-hidden">
            <MudCarpFish intensity={2} size={0.18} />
          </span>
          <span className="font-display font-bold text-2xl tracking-tight">
            Mud<span className="text-carp-primary">Carp</span>
          </span>
          <span className="hidden sm:inline-block chip">w sprzedaży</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-4">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `btn-ghost ${isActive ? "bg-carp-yellow" : ""}`
            }
          >
            O co biega
          </NavLink>
          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `btn-ghost ${isActive ? "bg-carp-cyan" : ""}`
              }
            >
              Admin
            </NavLink>
          )}
          {auth.loading ? (
            <span className="text-sm font-mono text-carp-ink/50">…</span>
          ) : auth.user ? (
            <UserMenu username={auth.user.kickUsername} onLogout={auth.logout} />
          ) : (
            <a href={KICK_LOGIN_URL} className="btn-ghost">
              <KickIcon /> Zaloguj
            </a>
          )}
          <NavLink to="/buy" className="btn-primary">
            Zagaduj
          </NavLink>
        </nav>
      </div>
    </header>
  );
}

function UserMenu({ username, onLogout }: { username: string; onLogout: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="hidden sm:inline-block chip bg-carp-lime">
        @{username}
      </span>
      <button
        type="button"
        onClick={onLogout}
        className="btn-ghost text-sm"
        title="Wyloguj"
      >
        Wyloguj
      </button>
    </div>
  );
}

function KickIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M3 3h5v5h2V6h2V4h2V3h5v5h-2v2h-2v2h-2v2h2v2h2v2h2v5h-5v-1h-2v-2h-2v-2h-2v5H3V3z" />
    </svg>
  );
}

function Footer() {
  return (
    <footer className="mt-20 border-t-[3px] border-carp-ink bg-carp-paper">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid gap-6 sm:grid-cols-4">
        <div>
          <p className="font-display font-bold text-xl">MudCarp</p>
          <p className="text-sm mt-1 text-carp-ink/70">
            Karp z głośnikiem. Karp z opinią. Karp na hajsie.
          </p>
          <p className="text-xs mt-3 text-carp-ink/60">
            Tailored Apps Łukasz Kowalski<br />
            ul. Gliniana 52A, 91-336 Łódź<br />
            NIP: PL7282614483
          </p>
        </div>
        <div className="text-sm space-y-1">
          <p className="font-bold uppercase font-display tracking-wide">Dokumenty</p>
          <p>
            <Link to="/regulamin" className="link-brut">Regulamin</Link>{" "}
            <span className="text-carp-ink/40 font-mono">v1</span>
          </p>
          <p>
            <Link to="/polityka-prywatnosci" className="link-brut">Polityka prywatności</Link>
          </p>
          <p className="text-xs text-carp-ink/60 mt-2">
            Sprzedaż treści cyfrowych — art. 38 ust. 13 u.p.k.
          </p>
        </div>
        <div className="text-sm space-y-1">
          <p className="font-bold uppercase font-display tracking-wide">Płatności</p>
          <p>BLIK · Karta · Przelewy24</p>
          <p className="text-xs text-carp-ink/60">
            Wyłącznie przez Stripe Payments Europe (Irlandia)
          </p>
          <p className="text-xs text-carp-ink/60">
            Faktura VAT na życzenie — Subiekt 123
          </p>
        </div>
        <div className="text-sm space-y-1">
          <p className="font-bold uppercase font-display tracking-wide">Kontakt</p>
          <p>
            <a href="mailto:kontakt@mudcarp.pl" className="link-brut">
              kontakt@mudcarp.pl
            </a>
          </p>
          <p className="text-xs text-carp-ink/60 mt-2">
            Reklamacje, faktury, dane osobowe — jeden mail.
          </p>
        </div>
      </div>
      <div className="stripe h-2 w-full" />
    </footer>
  );
}

export default Layout;
